/**
 * Main entry point — wires up DOM events and orchestrates the app flow.
 */

import './styles/main.css';
import { REQUIRED_APPROVALS } from './lib/constants.js';
import { setApiUrl } from './lib/rpc.js';
import { initLogger, log } from './lib/logger.js';
import { connectWallet, disconnectWallet, getSession, isConnected, restoreSession } from './lib/wallet.js';
import { fetchAllProducers, fetchActiveSchedule } from './lib/producers.js';
import {
  proposeMsigAction,
  approveProposal,
  executeProposal,
  cancelProposal,
  getProposalStatus,
} from './lib/msig.js';
import { initDropdown, setItems } from './lib/dropdown.js';

// ---------------------------------------------------------------------------
// Application state
// ---------------------------------------------------------------------------
const state = {
  allProducers: [],
  activeBPs: [],
  approvedBPs: new Set(),
  proposalExists: false,
};

// ---------------------------------------------------------------------------
// DOM references (populated in init)
// ---------------------------------------------------------------------------
const $ = (id) => document.getElementById(id);

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  // Logger
  initLogger($('logArea'));

  // Searchable dropdown
  initDropdown($('inputProducer'), $('producerDropdown'), (name) => {
    log(`Selected producer: ${name}`);
    updateProposalName();
    if (state.activeBPs.length > 0) renderBPTable();
  });

  // Also update proposal name on manual typing
  $('inputProducer').addEventListener('input', updateProposalName);

  // Wire up buttons
  $('btnConnect').addEventListener('click', handleConnect);
  $('btnFetchBPs').addEventListener('click', handleFetchBPs);
  $('btnPropose').addEventListener('click', handlePropose);
  $('btnCheckProposal').addEventListener('click', handleCheckProposal);
  $('btnApprove').addEventListener('click', handleApprove);
  $('btnExecute').addEventListener('click', handleExecute);
  $('btnRefresh').addEventListener('click', handleRefresh);
  $('btnCancel').addEventListener('click', handleCancel);

  // API endpoint change
  $('inputApi').addEventListener('change', () => {
    setApiUrl($('inputApi').value);
    state.allProducers = [];
    state.activeBPs = [];
    $('inputProducer').value = '';
    $('inputProducer').placeholder = 'Loading producers...';
    loadProducers();
  });

  // Initial producer load
  loadProducers();

  // Try to silently restore a previous wallet session
  restoreSession().then((restored) => {
    if (restored) {
      handleRestoredSession(restored.actor, restored.permission);
    }
  });
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getConfig() {
  const producer = $('inputProducer').value.trim().toLowerCase();
  return {
    contractCode: 'eosio',
    actionName: 'rmvproducer',
    producer,
    proposal: $('inputProposal').value.trim().toLowerCase(),
    expiryDays: parseInt($('inputExpiry').value) || 7,
  };
}

/** Auto-generate proposal name as kick.<producer> (max 12 chars). */
function updateProposalName() {
  const producer = $('inputProducer').value.trim().toLowerCase();
  if (producer) {
    const name = ('kick.' + producer).slice(0, 12);
    $('inputProposal').value = name;
  } else {
    $('inputProposal').value = '';
  }
}

function setLoading(btn, loading, text) {
  btn.disabled = loading;
  btn.innerHTML = loading ? `<span class="spinner"></span> ${text}` : text;
}

// ---------------------------------------------------------------------------
// Load producers into dropdown
// ---------------------------------------------------------------------------
async function loadProducers() {
  $('producerListStatus').textContent = 'Fetching producer list from chain...';

  try {
    state.allProducers = await fetchAllProducers();
    setItems(state.allProducers);
    $('inputProducer').placeholder = 'Search or select a producer...';
    $('producerListStatus').textContent = `${state.allProducers.length} producers loaded`;
  } catch (err) {
    log(`Failed to fetch producers for dropdown: ${err.message}`, 'error');
    $('producerListStatus').textContent = 'Failed to load — type manually';
    $('inputProducer').placeholder = 'Type producer name manually...';
  }
}

// ---------------------------------------------------------------------------
// Wallet
// ---------------------------------------------------------------------------
async function handleConnect() {
  const btn = $('btnConnect');
  setLoading(btn, true, 'Connecting...');

  try {
    const { actor, permission } = await connectWallet();

    // Replace connect button with status
    $('walletSection').innerHTML = `
      <div style="display:flex;align-items:center;gap:12px;">
        <span class="status status-green"><span class="status-dot"></span> ${actor}</span>
        <button class="btn btn-sm btn-outline" id="btnDisconnect"
          style="color:white;border-color:rgba(255,255,255,0.3);">Disconnect</button>
      </div>
    `;
    $('btnDisconnect').addEventListener('click', async () => {
      await disconnectWallet();
      location.reload();
    });

    $('walletStatus').className = 'alert alert-success mb-12';
    $('walletStatus').textContent = `Connected as ${actor}@${permission}. You can now propose, approve, or execute multisig proposals.`;

    // Enable action buttons
    $('btnPropose').disabled = false;
    $('btnApprove').disabled = false;
    $('btnExecute').disabled = false;
    $('btnCancel').disabled = false;
  } catch (err) {
    log(`Wallet connection failed: ${err.message}`, 'error');
    $('walletStatus').className = 'alert alert-warn mb-12';
    $('walletStatus').innerHTML = `
      Wallet connection unavailable (SDK may not be loaded from CDN in this environment).
      <br>The tool will work in <strong>read-only mode</strong> — you can view BPs, check proposal status, and copy cleos commands.
      <br><br><strong>To use full on-chain interaction</strong>, host this file on a web server where the ProtonWebSDK CDN is accessible.
    `;
    btn.disabled = false;
    btn.textContent = 'Retry Connection';
  }
}

function handleRestoredSession(actor, permission) {
  $('walletSection').innerHTML = `
    <div style="display:flex;align-items:center;gap:12px;">
      <span class="status status-green"><span class="status-dot"></span> ${actor}</span>
      <button class="btn btn-sm btn-outline" id="btnDisconnect"
        style="color:white;border-color:rgba(255,255,255,0.3);">Disconnect</button>
    </div>
  `;
  $('btnDisconnect').addEventListener('click', async () => {
    await disconnectWallet();
    location.reload();
  });

  $('walletStatus').className = 'alert alert-success mb-12';
  $('walletStatus').textContent = `Connected as ${actor}@${permission}. You can now propose, approve, or execute multisig proposals.`;

  $('btnPropose').disabled = false;
  $('btnApprove').disabled = false;
  $('btnExecute').disabled = false;
  $('btnCancel').disabled = false;
}

// ---------------------------------------------------------------------------
// Fetch active BPs (step 2 table)
// ---------------------------------------------------------------------------
async function handleFetchBPs() {
  const btn = $('btnFetchBPs');
  setLoading(btn, true, 'Fetching...');

  try {
    state.activeBPs = await fetchActiveSchedule(state.allProducers);
    renderBPTable();

    $('stepBPs').classList.remove('hidden');
    $('step1Num').classList.add('done');
    $('step1Num').textContent = '\u2713';
    $('bpCount').textContent = `${state.activeBPs.length} producers`;
  } catch (err) {
    log(`Failed to fetch BPs: ${err.message}`, 'error');
  } finally {
    setLoading(btn, false, 'Fetch Active Block Producers');
  }
}

function renderBPTable() {
  const { producer } = getConfig();
  $('bpTableBody').innerHTML = state.activeBPs
    .map((bp, i) => {
      const isTarget = bp === producer;
      return `
      <tr class="${isTarget ? 'target-bp' : ''}">
        <td>${i + 1}</td>
        <td class="mono">${bp}</td>
        <td>${
          isTarget
            ? '<span class="status status-red"><span class="status-dot"></span> TARGET</span>'
            : '<span class="status status-green"><span class="status-dot"></span> Active</span>'
        }</td>
        <td><input type="checkbox" checked data-bp="${bp}" class="bp-checkbox"></td>
      </tr>`;
    })
    .join('');
}

// ---------------------------------------------------------------------------
// Propose
// ---------------------------------------------------------------------------
async function handlePropose() {
  if (!isConnected()) return log('Wallet not connected. Please connect first.', 'error');

  const { contractCode, actionName, producer, proposal, expiryDays } = getConfig();
  const checkboxes = document.querySelectorAll('.bp-checkbox:checked');
  const requested = Array.from(checkboxes).map((cb) => ({
    actor: cb.dataset.bp,
    permission: 'active',
  }));

  const btn = $('btnPropose');
  setLoading(btn, true, 'Proposing...');

  try {
    await proposeMsigAction({
      contractCode,
      actionName,
      actionArgs: { producer },
      proposalName: proposal,
      requested,
      expiryDays,
    });
    state.proposalExists = true;
    showApprovalPanel(requested);
  } catch (err) {
    log(`Propose failed: ${err.message}`, 'error');
    if (err.message?.includes('assertion failure')) {
      log(
        'This often means a proposal with this name already exists. Try a different name or cancel the existing one.',
        'warn'
      );
    }
  } finally {
    setLoading(btn, false, 'Propose Msig');
  }
}

// ---------------------------------------------------------------------------
// Check existing proposal
// ---------------------------------------------------------------------------
async function handleCheckProposal() {
  const { proposal } = getConfig();
  const session = getSession();
  let proposer;

  if (session) {
    proposer = session.auth.actor.toString();
  } else {
    proposer = prompt('Enter the proposer account name:', 'protonind');
    if (!proposer) return;
  }

  try {
    const status = await getProposalStatus(proposer, proposal);
    if (!status.exists) return;

    state.proposalExists = true;
    state.approvedBPs = new Set(status.approved);
    showApprovalPanel(status.requested);
  } catch (err) {
    log(`Error checking proposal: ${err.message}`, 'error');
  }
}

// ---------------------------------------------------------------------------
// Approval panel
// ---------------------------------------------------------------------------
function showApprovalPanel(requested) {
  $('stepApprovals').classList.remove('hidden');
  $('step2Num').classList.add('done');
  $('step2Num').textContent = '\u2713';

  const { contractCode, actionName, proposal, producer } = getConfig();
  const session = getSession();
  const proposer = session ? session.auth.actor.toString() : 'protonind';

  $('proposalInfo').innerHTML = `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:8px;">
      <div class="alert alert-info" style="margin:0;">
        <strong>Proposal:</strong> ${proposal}<br>
        <strong>Proposer:</strong> ${proposer}
      </div>
      <div class="alert alert-error" style="margin:0;">
        <strong>Action:</strong> ${contractCode}::${actionName}<br>
        <strong>Target:</strong> ${producer}
      </div>
    </div>
  `;

  renderApprovalGrid(requested);
}

function renderApprovalGrid(requested) {
  const total = requested.length;
  const count = state.approvedBPs.size;
  const pct = Math.round((count / REQUIRED_APPROVALS) * 100);

  $('approvalCount').textContent = `${count} / ${REQUIRED_APPROVALS} required (of ${total})`;
  $('progressBar').style.width = Math.min(pct, 100) + '%';
  $('progressText').textContent =
    count >= REQUIRED_APPROVALS
      ? `READY TO EXECUTE (${count}/${total})`
      : `${count} / ${REQUIRED_APPROVALS}`;

  $('approvalGrid').innerHTML = requested
    .map((r) => {
      const ok = state.approvedBPs.has(r.actor);
      return `
      <div class="approval-item ${ok ? 'approved' : ''}">
        <span class="mono">${r.actor}</span>
        ${
          ok
            ? '<span class="status status-green" style="padding:2px 6px;font-size:11px;">\u2713 Approved</span>'
            : '<span class="status status-gray" style="padding:2px 6px;font-size:11px;">Pending</span>'
        }
      </div>`;
    })
    .join('');
}

// ---------------------------------------------------------------------------
// Approve
// ---------------------------------------------------------------------------
async function handleApprove() {
  if (!isConnected()) return log('Wallet not connected.', 'error');

  const { proposal } = getConfig();
  const session = getSession();
  const actor = session.auth.actor.toString();

  let proposer = actor;
  const input = prompt('Enter the proposer account name (who created the proposal):', actor);
  if (input) proposer = input;

  const btn = $('btnApprove');
  setLoading(btn, true, 'Approving...');

  try {
    await approveProposal(proposer, proposal);
    state.approvedBPs.add(actor);

    // Re-render approval grid
    if (!$('stepApprovals').classList.contains('hidden')) {
      const requested =
        state.activeBPs.length > 0
          ? state.activeBPs.map((b) => ({ actor: b, permission: 'active' }))
          : [];
      if (requested.length > 0) renderApprovalGrid(requested);
    }
  } catch (err) {
    log(`Approve failed: ${err.message}`, 'error');
  } finally {
    setLoading(btn, false, 'Approve Proposal');
  }
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------
async function handleExecute() {
  if (!isConnected()) return log('Wallet not connected.', 'error');

  const { actionName, proposal, producer } = getConfig();
  const session = getSession();
  const actor = session.auth.actor.toString();

  let proposer = actor;
  const input = prompt('Enter the proposer account name:', actor);
  if (input) proposer = input;

  if (state.approvedBPs.size < REQUIRED_APPROVALS) {
    const ok = confirm(
      `Only ${state.approvedBPs.size} approvals detected (${REQUIRED_APPROVALS} required). Execute anyway?`
    );
    if (!ok) return;
  }

  const btn = $('btnExecute');
  setLoading(btn, true, 'Executing...');

  try {
    await executeProposal(proposer, proposal);
    log(`Action "${actionName}" executed successfully for "${producer}".`, 'success');
    $('step3Num').classList.add('done');
    $('step3Num').textContent = '\u2713';
  } catch (err) {
    log(`Execute failed: ${err.message}`, 'error');
    if (err.message?.includes('transaction authorization failed')) {
      log('Not enough approvals yet. Need 15/21 BP approvals.', 'warn');
    }
  } finally {
    setLoading(btn, false, 'Execute Proposal');
  }
}

// ---------------------------------------------------------------------------
// Refresh / Cancel
// ---------------------------------------------------------------------------
async function handleRefresh() {
  log('Refreshing approval status...');
  await handleCheckProposal();
}

async function handleCancel() {
  if (!isConnected()) return log('Wallet not connected.', 'error');

  const { proposal } = getConfig();
  if (!confirm(`Cancel proposal "${proposal}"? Only the original proposer can cancel.`)) return;

  try {
    await cancelProposal(proposal);
    state.proposalExists = false;
  } catch (err) {
    log(`Cancel failed: ${err.message}`, 'error');
  }
}
